<template>
  <div
    class="sidebar bg-info"
    :class="{ 'sidebar--solid': !site || !site.menuBackgroundImage }"
    :data-color="activeColor"
    :data-image="backgroundImage"
    :data-background-color="backgroundColor"
    :style="sidebarStyle"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="logo sidebar-logo-row">
      <div class="sidebar-logo-brand">
        <a href="/" class="simple-text logo-mini">
          <div class="logo-img">
            <img :src="site.logo" width="60" height="60" />
          </div>
        </a>
        <a href="/" class="simple-text logo-normal">
          <div v-if="site">
            <small v-if="$route.meta.rtlActive">{{ site.siteName }}</small>
            <small v-else>{{ site.siteName }}</small>
          </div>
        </a>
      </div>
    </div>
    <div class="sidebar-wrapper" ref="sidebarScrollArea">
      <slot></slot>
      <ul class="md-list nav">
        <slot name="links"> </slot>
        <dark-mode-switch>
          <zoom-control />
        </dark-mode-switch>
      </ul>
    </div>
  </div>
</template>
<script>
import Swal from "sweetalert2"; // Importa SweetAlert
import { wencesApi } from "@/api/wencesApi";
import DarkModeSwitch from "@/components/DarkModeSwitch.vue";
import ZoomControl from "@/components/ZoomControl.vue";

export default {
  name: "sidebar",
  components: {
    DarkModeSwitch,
    ZoomControl
  },
  props: {
    activeColor: {
      type: String,
      default: "azure",
      validator: (value) => {
        let acceptedValues = [
          "",
          "purple",
          "azure",
          "green",
          "orange",
          "danger",
          "rose",
        ];
        return acceptedValues.indexOf(value) !== -1;
      },
    },
    backgroundImage: {
      type: String,
      default: process.env.BASE_URL + "img/sidebar-2.jpg",
    },
    backgroundColor: {
      type: String,
      default: "black",
    },
    logo: {
      type: String,
      default: process.env.BASE_URL + "img/icons/favicon-32x32.png",
    },
    sidebarLinks: {
      type: Array,
      default: () => [],
    },
    autoClose: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      allStablishments: false,
      stablishment: false,
      loading: true,
      site: false,
    };
  },
  async created() {
    document.documentElement.setAttribute("class", "dark");

    // Inicializar sidebar en estado minimizado
    document.body.classList.add("sidebar-mini");

    let subdomain = window.location.hostname;
    const parts = subdomain.split(".");

    this.role = localStorage.getItem("role");

    const response = await wencesApi.get("/api/settings");

    this.site = response.data.data;
  },
  provide() {
    return {
      autoClose: this.autoClose,
    };
  },
  methods: {
    minimizeSidebar() {
      if (this.$sidebar) {
        this.$sidebar.toggleMinimize();
      }
    },
    onMouseEnter() {
      // Expandir el sidebar si está contraído
      if (this.$sidebar && !this.$sidebar.isExpanded) {
        this.$sidebar.toggleExpanded();
      }
    },
    onMouseLeave() {
      // Contraer el sidebar si está expandido
      if (this.$sidebar && this.$sidebar.isExpanded) {
        this.$sidebar.toggleExpanded();
      }
    },
  },
  watch: {
    // En el teléfono, con el menú abierto el body (que es el que desplaza) queda quieto
    // y el gesto se lo lleva la lista del menú.
    "$sidebar.showSidebar": {
      immediate: true,
      handler(open) {
        document.body.classList.toggle(
          "m-sidebar-open",
          !!open && window.innerWidth < 992
        );
      },
    },
  },
  computed: {
    sidebarStyle() {
      // La imagen solo si el tenant cargó una en Ajustes; si no, fondo liso
      // (`.sidebar--solid` en modern-touch.css).
      const bgImage = this.site?.menuBackgroundImage;
      return bgImage ? { backgroundImage: `url(${bgImage})` } : {};
    },
  },
  beforeDestroy() {
    document.body.classList.remove("m-sidebar-open");
    if (this.$sidebar.showSidebar) {
      this.$sidebar.showSidebar = false;
    }
  },
};
</script>
<style>
@media (min-width: 992px) {
  .navbar-search-form-mobile,
  .nav-mobile-menu {
    display: none;
  }
}
@media (max-width: 991px) {
  /*
   * En el teléfono el menú mide lo visible (100dvh, no 100vh, que incluye la barra del
   * navegador) y la lista ocupa lo que deja el logo, en vez de su `100vh - 75px` fijo.
   * Sin esto el final (modo oscuro, zoom) quedaba fuera de pantalla o bajo la barra inferior.
   */
  .wrapper > .sidebar {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
  }
  .wrapper > .sidebar > .logo {
    flex: 0 0 auto;
  }
  html body .wrapper .sidebar .sidebar-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    height: auto !important;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  }
  /* La barra inferior (MobileBottomNav) queda encima del menú: lugar para ella. */
  html body.m-has-bottom-nav .wrapper .sidebar .sidebar-wrapper {
    padding-bottom: calc(3.6rem + 1.5rem + env(safe-area-inset-bottom));
  }
  /* Con el menú abierto la página de atrás no se desplaza. */
  body.m-sidebar-open {
    overflow: hidden !important;
  }
}
</style>
<style>
.loadingabsolute {
  position: absolute;
  width: 30000px;
  height: 3000px;
  background: rgba(0, 0, 0, 1); /* Fondo oscuro con opacidad */
  z-index: 9999;
  display: block;
  float: left;
  top: 0;
  opacity: 0.9;
}
.sidebar .logo.sidebar-logo-row {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}
.sidebar-logo-brand {
  width: 100%;
  min-width: 0;
}
.logomenu {
  background-repeat: no-repeat !important;
  background-size: cover !important;
  background-position: center !important;
  background-color: white !important;
  width: 80px !important;
  border-radius: 40%;
  -webkit-box-shadow: 0px 0px 14px -4px;
  box-shadow: 0px 0px 14px -4px;
  display: inline-block;

  height: 37px !important;
  border-radius: 0 !important;
  border: 0 !important;
}
</style>
